import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Plus, ChevronDown } from "lucide-react";
import { useAuth, ROLE_THEMES } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";

/**
 * RoleSwitcher — pill in the AppShell header. Lets the same account switch
 * between Farmer / Day Worker / Investor workspaces without a fresh login.
 * Backend: POST /api/users/roles/switch (must be in `roles` first).
 * Adding a role: POST /api/users/roles/add.
 */
const ALL_ROLES = [
  { k: "farmer", label: "Farmer", icon: "🌾" },
  { k: "day_worker", label: "Day Worker", icon: "👷" },
  { k: "investor", label: "Investor", icon: "💰" },
];

export default function RoleSwitcher() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const wrapRef = useRef();

  useEffect(() => {
    const onClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  if (!user) return null;
  const activeRole = user.active_role || user.role;
  const myRoles = user.roles?.length ? user.roles : [user.role];
  const activeTheme = ROLE_THEMES[activeRole] || ROLE_THEMES.farmer;
  const activeMeta = ALL_ROLES.find((r) => r.k === activeRole);

  const doSwitch = async (r) => {
    if (busy || r === activeRole) { setOpen(false); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/users/roles/switch", { role: r });
      setUser(data);
      toast.success(`Switched to ${ALL_ROLES.find((x) => x.k === r)?.label} workspace`);
      setOpen(false);
      nav("/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const doAdd = async (r) => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/users/roles/add", { role: r });
      setUser(data);
      toast.success(`${ALL_ROLES.find((x) => x.k === r)?.label} role added — tap to switch`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div ref={wrapRef} className="relative">
      <button data-testid="role-switcher-btn" onClick={() => setOpen(!open)}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${activeTheme.bgSoft} ${activeTheme.text} border ${activeTheme.border}`}>
        <span className="text-base leading-none">{activeMeta?.icon}</span>
        <span className="hidden sm:inline">{activeMeta?.label}</span>
        <ChevronDown className="h-3 w-3" />
      </button>
      {open && (
        <div data-testid="role-switcher-menu" className="absolute right-0 mt-2 w-64 rounded-2xl bg-white border border-gray-100 shadow-2xl overflow-hidden z-50">
          <div className="px-4 py-2 text-[10px] uppercase tracking-widest text-gray-500 border-b border-gray-50">Switch workspace</div>
          {ALL_ROLES.map((r) => {
            const has = myRoles.includes(r.k);
            const isActive = r.k === activeRole;
            return (
              <button key={r.k} data-testid={`role-switch-${r.k}`}
                onClick={() => has ? doSwitch(r.k) : doAdd(r.k)}
                disabled={busy}
                className={`w-full flex items-center gap-3 px-4 py-3 text-sm hover:bg-gray-50 border-b border-gray-50 last:border-b-0 ${isActive ? "bg-emerald-50" : ""}`}>
                <span className="text-lg">{r.icon}</span>
                <div className="flex-1 text-left">
                  <div className="font-semibold text-gray-900">{r.label}</div>
                  {!has && <div className="text-[10px] text-gray-500">Tap to add this role to your account</div>}
                </div>
                {isActive ? <Check className="h-4 w-4 text-emerald-600" /> : has ? <div className="text-[10px] text-gray-400">Switch</div> : <Plus className="h-4 w-4 text-emerald-600" />}
              </button>
            );
          })}
          <div className="px-4 py-2 text-[10px] text-gray-500 bg-gray-50">One account · Same profile · Same messages</div>
        </div>
      )}
    </div>
  );
}
